import { formatNative, formatUsd } from './optionsDashboardUtils';
import { formatSignedUsd } from './optionsPerformanceUtils';
import { formatLocalizedNumber, getOptionsLanguageCode } from './optionsLocaleFormatting';

export const PORTFOLIO_RISK_ASSET_COLORS = {
  BTC: 'from-[#F7931A] to-[#FFB85C]',
  ETH: 'from-[#627EEA] to-[#AAB7FF]',
  USDC: 'from-[#2775CA] to-[#67A8EA]',
  USDT: 'from-[#26A17B] to-[#64D0AB]',
  DEFAULT: 'from-white/40 to-white/70',
};

export const POSITION_BIAS_SEGMENTS = [
  { key: 'strong-bearish', labelKey: 'positions.strongBearish', className: 'bg-[#7F1D1D]' },
  { key: 'bearish', labelKey: 'positions.bearish', className: 'bg-[#B91C1C]' },
  { key: 'neutral', labelKey: 'positions.neutral', className: 'bg-[#475569]' },
  { key: 'bullish', labelKey: 'positions.bullish', className: 'bg-[#15803D]' },
  { key: 'strong-bullish', labelKey: 'positions.strongBullish', className: 'bg-[#166534]' },
];

export const RISK_SUMMARY_DISPLAY_ORDER = ['BTC', 'ETH', 'USDC'];
export const STABLE_CURRENCY_KEYS = ['USD', 'USDC', 'USDT', 'BUSD', 'DAI', 'FDUSD', 'PYUSD', 'TUSD', 'USDE'];

const EXPOSURE_MIN = -0.3;
const EXPOSURE_MAX = 0.3;

const getDigitsForCurrency = (currency) => (String(currency || '').toUpperCase() === 'BTC' ? 4 : 2);

export const formatSignedNative = (value, currency, digits = getDigitsForCurrency(currency)) => {
  if (value === null || value === undefined) return '—';
  const base = formatNative(value, currency, digits);
  return Number(value) > 0 ? `+${base}` : base;
};

export const formatSignedPercent = (value) => {
  if (value === null || value === undefined) return '—';
  const percentage = formatLocalizedNumber(Number(value) * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `${Number(value) > 0 ? '+' : ''}${percentage}%`;
};

export const formatUnsignedPercent = (value) => {
  if (value === null || value === undefined) return '—';
  return `${formatLocalizedNumber(Number(value) * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

export const formatThetaPerDay = (value, digits = 2) => {
  if (value === null || value === undefined) return '—';
  const suffix = getOptionsLanguageCode() === 'pt' ? 'dia' : 'day';
  return `${formatSignedUsd(value, digits)} / ${suffix}`;
};

export const formatVegaPerIvPoint = (value, digits = 2) => {
  if (value === null || value === undefined) return '—';
  return `${formatSignedUsd(value, digits)} / 1% IV`;
};

// Gamma-only: isolated from the shared resolveAdaptiveFractionDigits (used broadly by
// Delta/Theta/Vega/native formatters) so this floor can go deeper without touching those.
const resolveGammaFractionDigits = (numeric) => {
  if (!Number.isFinite(numeric)) return 6;
  if (numeric >= 0.0001) return 6;
  return 10;
};

export const formatSignedPlainNumber = (value, requestedDigits = 6) => {
  if (value === null || value === undefined) return '—';
  const numeric = Number(value);
  if (numeric === 0) return '0';
  const maximumFractionDigits = Math.max(requestedDigits, resolveGammaFractionDigits(Math.abs(numeric)));
  const formatted = formatLocalizedNumber(Math.abs(numeric), { minimumFractionDigits: 0, maximumFractionDigits });
  return numeric > 0 ? `+${formatted}` : `-${formatted}`;
};

const GAMMA_RATIO_PRICE_STEP_USD = 1000;

// Presentation-only helper: Δ Delta ≈ Gamma × ΔPrice, for a US$1k underlying move.
// Does not persist anywhere and does not feed any other calculation.
export const formatGammaDeltaRatio = (rawGamma) => {
  const numeric = Number(rawGamma);
  if (rawGamma === null || rawGamma === undefined || !Number.isFinite(numeric) || numeric === 0) return null;
  const deltaChange = numeric * GAMMA_RATIO_PRICE_STEP_USD;
  return `\u2248 ${formatSignedPlainNumber(deltaChange)} \u0394 / US$ 1k`;
};

export const isStableCurrency = (currency) => STABLE_CURRENCY_KEYS.includes(String(currency || '').toUpperCase());

export const getBiasMeta = (exposurePct) => {
  if (exposurePct === null || exposurePct === undefined) {
    return {
      key: 'unavailable',
      labelKey: 'positions.awaitingValidation',
      badgeClassName: 'border-white/10 bg-white/[0.05] text-white/70',
    };
  }

  if (exposurePct > 0.2) {
    return {
      key: 'strong-bullish',
      labelKey: 'positions.strongBullish',
      badgeClassName: 'border-[#22C55E]/20 bg-[#22C55E]/12 text-[#BDFACD]',
    };
  }
  if (exposurePct > 0.05) {
    return {
      key: 'bullish',
      labelKey: 'positions.bullish',
      badgeClassName: 'border-[#4ADE80]/20 bg-[#4ADE80]/12 text-[#D1FADF]',
    };
  }
  if (exposurePct < -0.2) {
    return {
      key: 'strong-bearish',
      labelKey: 'positions.strongBearish',
      badgeClassName: 'border-[#EF4444]/20 bg-[#EF4444]/12 text-[#FFD2D2]',
    };
  }
  if (exposurePct < -0.05) {
    return {
      key: 'bearish',
      labelKey: 'positions.bearish',
      badgeClassName: 'border-[#F87171]/20 bg-[#F87171]/12 text-[#FFE1E1]',
    };
  }

  return {
    key: 'neutral',
    labelKey: 'positions.neutral',
    badgeClassName: 'border-white/10 bg-white/[0.05] text-white',
  };
};

export const getRiskSummaryBiasMeta = (exposurePct) => {
  if (exposurePct === null || exposurePct === undefined) {
    return {
      key: 'unavailable',
      labelKey: null,
      badgeClassName: 'border-white/10 bg-white/[0.04] text-white/60',
    };
  }

  if (Number(exposurePct) <= -0.3) {
    return {
      key: 'strongly-bearish',
      labelKey: 'positions.stronglyBearish',
      badgeClassName: 'border-[#EF4444]/20 bg-[#EF4444]/12 text-[#FFD2D2]',
    };
  }

  if (Number(exposurePct) <= -0.2) {
    return {
      key: 'bearish',
      labelKey: 'positions.bearish',
      badgeClassName: 'border-[#F87171]/18 bg-[#F87171]/10 text-[#FFE3E3]',
    };
  }

  if (Number(exposurePct) < -0.1) {
    return {
      key: 'partially-bearish',
      labelKey: 'positions.partiallyBearish',
      badgeClassName: 'border-[#FB923C]/18 bg-[#FB923C]/10 text-[#FED7AA]',
    };
  }

  if (Number(exposurePct) <= 0.1) {
    return {
      key: 'neutral',
      labelKey: 'positions.neutral',
      badgeClassName: 'border-white/10 bg-white/[0.05] text-white/78',
    };
  }

  if (Number(exposurePct) < 0.2) {
    return {
      key: 'partially-bullish',
      labelKey: 'positions.partiallyBullish',
      badgeClassName: 'border-[#4ADE80]/18 bg-[#4ADE80]/10 text-[#D8FBE1]',
    };
  }

  if (Number(exposurePct) < 0.3) {
    return {
      key: 'bullish',
      labelKey: 'positions.bullish',
      badgeClassName: 'border-[#22C55E]/20 bg-[#22C55E]/12 text-[#BDFACD]',
    };
  }

  return {
    key: 'strongly-bullish',
    labelKey: 'positions.stronglyBullish',
    badgeClassName: 'border-[#16A34A]/20 bg-[#16A34A]/12 text-[#BBF7D0]',
  };
};

export const getBiasMarkerPosition = (exposurePct) => {
  if (exposurePct === null || exposurePct === undefined) return null;
  const clamped = Math.max(EXPOSURE_MIN, Math.min(EXPOSURE_MAX, Number(exposurePct)));
  return ((clamped - EXPOSURE_MIN) / (EXPOSURE_MAX - EXPOSURE_MIN)) * 100;
};

const getRawWalletAssets = (accountData) => {
  const demoWalletAssets = accountData?.portfolio_risk_demo?.wallet_assets || [];
  if (demoWalletAssets.length) return demoWalletAssets;

  return (accountData?.account_equity?.currencies || []).map((item) => ({
    currency: item.currency,
    native_value: item.equity_native,
    value_usd: item.equity_usd,
    usd_price: item.usd_price,
  }));
};

const getUsdPrice = ({ nativeValue, valueUsd, usdPrice }) => {
  if (usdPrice !== null && usdPrice !== undefined) return Number(usdPrice);
  if (!nativeValue) return null;
  return Number(valueUsd || 0) / Number(nativeValue);
};

const getExposureDisplayOrder = (currencies = []) => {
  const unique = Array.from(new Set(currencies.map((currency) => String(currency).toUpperCase()).filter(Boolean)));
  const preferred = RISK_SUMMARY_DISPLAY_ORDER.filter((currency) => unique.includes(currency));
  const remainingDirectional = unique
    .filter((currency) => !preferred.includes(currency) && !isStableCurrency(currency))
    .sort((a, b) => a.localeCompare(b));
  const stables = unique
    .filter((currency) => !preferred.includes(currency) && isStableCurrency(currency))
    .sort((a, b) => a.localeCompare(b));

  return [...preferred, ...remainingDirectional, ...stables];
};

export const getWalletExposureItems = (accountData) => {
  const rawItems = getRawWalletAssets(accountData);
  const filtered = rawItems.filter((item) => Number(item.value_usd || 0) > 0);
  const totalUsd = filtered.reduce((sum, item) => sum + Number(item.value_usd || 0), 0);

  return getExposureDisplayOrder(filtered.map((item) => item.currency)).map((currency) => {
    const item = filtered.find((entry) => String(entry.currency).toUpperCase() === currency);
    const percent = totalUsd > 0 ? Number(item?.value_usd || 0) / totalUsd : 0;
    const stable = isStableCurrency(currency);

    return {
      currency,
      nativeValue: Number(item?.native_value || 0),
      valueUsd: Number(item?.value_usd || 0),
      percent,
      percentLabel: formatUnsignedPercent(percent),
      colorClassName: PORTFOLIO_RISK_ASSET_COLORS[currency] || PORTFOLIO_RISK_ASSET_COLORS.DEFAULT,
      valueLabel: formatNative(item?.native_value, currency, getDigitsForCurrency(currency)),
      usdLabel: formatUsd(item?.value_usd),
      usdPrice: getUsdPrice({ nativeValue: item?.native_value, valueUsd: item?.value_usd, usdPrice: item?.usd_price }),
      isStable: stable,
      directionalNativeValue: stable ? 0 : Number(item?.native_value || 0),
      directionalUsdValue: stable ? 0 : Number(item?.value_usd || 0),
      bias: stable ? getRiskSummaryBiasMeta(0) : getRiskSummaryBiasMeta(percent),
    };
  });
};

export const buildPositionRiskRow = (item, exposureValidated) => {
  const exposurePct = exposureValidated && item.delta_total !== null && item.delta_total !== undefined && item.equity_native
    ? Number(item.delta_total) / Number(item.equity_native)
    : null;
  const summaryBias = getRiskSummaryBiasMeta(exposurePct);

  return {
    currency: item.currency,
    rawNetDelta: item.delta_total ?? null,
    rawGamma: item.options_gamma ?? null,
    rawTheta: item.options_theta ?? null,
    rawVega: item.options_vega ?? null,
    equityNative: item.equity_native ?? null,
    equityUsd: item.equity_usd ?? null,
    usdPrice: item.usd_price ?? (item.equity_native ? Number(item.equity_usd || 0) / Number(item.equity_native) : null),
    rawExposurePct: exposurePct,
    netDelta: formatSignedNative(item.delta_total, item.currency, 4),
    gamma: formatSignedPlainNumber(item.options_gamma),
    gammaRatioLabel: formatGammaDeltaRatio(item.options_gamma),
    theta: formatThetaPerDay(item.options_theta),
    vega: formatVegaPerIvPoint(item.options_vega),
    exposurePct,
    exposureLabel: formatSignedPercent(exposurePct),
    markerPosition: getBiasMarkerPosition(exposurePct),
    bias: getBiasMeta(exposurePct),
    summaryBias,
  };
};

const hasStructuredPortfolioExposure = (accountData) => Array.isArray(accountData?.portfolio_exposure?.rows) && accountData.portfolio_exposure.rows.length > 0;

const buildStructuredExposureRow = (row, key) => {
  const exposurePct = row?.[`${key}_pct`] ?? null;
  const nativeValue = row?.[`${key}_native`] ?? null;
  return {
    currency: row.currency,
    exposurePct,
    exposureLabel: exposurePct === null || exposurePct === undefined ? '—' : formatSignedPercent(exposurePct),
    markerPosition: getBiasMarkerPosition(exposurePct),
    summaryBias: getRiskSummaryBiasMeta(exposurePct),
    rawNativeValue: nativeValue,
    nativeLabel: nativeValue === null || nativeValue === undefined ? '—' : formatSignedNative(nativeValue, row.currency, 4),
    usdPrice: row.usd_price ?? null,
  };
};

export const getPositionRiskRows = (accountData, { includeCurrencies = null } = {}) => {
  if (hasStructuredPortfolioExposure(accountData)) {
    const greeksByCurrency = new Map(
      (accountData?.account_equity?.currencies || [])
        .filter((entry) => entry?.currency)
        .map((entry) => [String(entry.currency).toUpperCase(), entry])
    );
    const rows = (accountData?.portfolio_exposure?.rows || []).map((row) => {
      const greeksEntry = greeksByCurrency.get(String(row.currency).toUpperCase()) || null;
      const rawGamma = greeksEntry?.options_gamma ?? null;
      const rawTheta = greeksEntry?.options_theta ?? null;
      const rawVega = greeksEntry?.options_vega ?? null;
      return {
        currency: row.currency,
        rawNetDelta: row.options_exposure_native ?? null,
        rawGamma,
        rawTheta,
        rawVega,
        equityNative: row.wallet_exposure_native ?? null,
        equityUsd: row.wallet_exposure_usd ?? null,
        usdPrice: row.usd_price ?? null,
        exposurePct: row.options_exposure_pct ?? null,
        exposureLabel: row.options_exposure_pct === null || row.options_exposure_pct === undefined ? '—' : formatSignedPercent(row.options_exposure_pct),
        markerPosition: getBiasMarkerPosition(row.options_exposure_pct ?? null),
        bias: getBiasMeta(row.options_exposure_pct ?? null),
        summaryBias: getRiskSummaryBiasMeta(row.options_exposure_pct ?? null),
        netDelta: formatSignedNative(row.options_exposure_native, row.currency, 4),
        gamma: formatSignedPlainNumber(rawGamma),
        gammaRatioLabel: formatGammaDeltaRatio(rawGamma),
        theta: formatThetaPerDay(rawTheta),
        vega: formatVegaPerIvPoint(rawVega),
      };
    });
    if (!includeCurrencies?.length) return rows;
    const includeSet = new Set(includeCurrencies.map((currency) => String(currency).toUpperCase()));
    return rows.filter((row) => includeSet.has(String(row.currency).toUpperCase()));
  }

  const currencies = accountData?.account_equity?.currencies || [];
  const exposureValidated = Boolean(accountData?.portfolio_risk_demo?.position_exposure_validated);
  const includeSet = includeCurrencies?.length
    ? new Set(includeCurrencies.map((currency) => String(currency).toUpperCase()))
    : null;

  const rows = currencies
    .filter((item) => item.currency)
    .filter((item) => !includeSet || includeSet.has(String(item.currency).toUpperCase()))
    .map((item) => buildPositionRiskRow(item, exposureValidated));

  if (!includeSet) return rows;

  const rowByCurrency = new Map(rows.map((row) => [String(row.currency).toUpperCase(), row]));
  return includeCurrencies
    .map((currency) => rowByCurrency.get(String(currency).toUpperCase()))
    .filter(Boolean);
};

export const getFuturesRiskRows = (accountData) => {
  if (!hasStructuredPortfolioExposure(accountData)) return [];
  return (accountData?.portfolio_exposure?.rows || []).map((row) => ({
    currency: row.currency,
    netDelta: formatSignedNative(row.futures_exposure_native, row.currency, 4),
    exposurePct: row.futures_exposure_pct ?? null,
    exposureLabel: row.futures_exposure_pct === null || row.futures_exposure_pct === undefined ? '—' : formatSignedPercent(row.futures_exposure_pct),
    summaryBias: getRiskSummaryBiasMeta(row.futures_exposure_pct ?? null),
    markerPosition: getBiasMarkerPosition(row.futures_exposure_pct ?? null),
  })).filter((row) => Number(row.exposurePct || 0) !== 0 || row.netDelta !== '—');
};

export const getFuturesHedgeDeltaByCurrency = (accountData) => {
  const positions = accountData?.management_positions || [];
  const map = new Map();
  positions.forEach((position) => {
    if (String(position?.effective_role || '').toUpperCase() !== 'HEDGE') return;
    const currency = String(position?.underlying || position?.currency || '').toUpperCase();
    if (!currency) return;
    const deltaEquivalent = Number(position?.delta_equivalent || 0);
    map.set(currency, (map.get(currency) || 0) + deltaEquivalent);
  });
  return map;
};

export const getCompactRiskSummaryRows = ({ accountData, openItems = [], underlyingFilter = 'all' }) => {
  const openCurrencies = new Set(
    openItems
      .map((item) => String(item?.underlying || '').toUpperCase())
      .filter(Boolean)
  );

  if (!openCurrencies.size) return [];

  const normalizedFilter = String(underlyingFilter || 'all').toUpperCase();
  const visibleCurrencies = normalizedFilter === 'ALL'
    ? RISK_SUMMARY_DISPLAY_ORDER.filter((currency) => openCurrencies.has(currency))
    : RISK_SUMMARY_DISPLAY_ORDER.filter((currency) => currency === normalizedFilter && openCurrencies.has(currency));

  if (!visibleCurrencies.length) return [];

  const rows = getPositionRiskRows(accountData, { includeCurrencies: visibleCurrencies });
  const hedgeDeltaByCurrency = getFuturesHedgeDeltaByCurrency(accountData);

  return rows.map((row) => {
    const currencyKey = String(row.currency).toUpperCase();
    if (!hedgeDeltaByCurrency.has(currencyKey)) {
      return { ...row, hasFuturesHedge: false };
    }
    const rawNetDeltaOptions = row.rawNetDelta ?? 0;
    const rawNetDeltaFutures = hedgeDeltaByCurrency.get(currencyKey) || 0;
    const rawNetDeltaTotal = Number(rawNetDeltaOptions) + Number(rawNetDeltaFutures);
    return {
      ...row,
      hasFuturesHedge: true,
      rawNetDeltaOptions,
      rawNetDeltaFutures,
      rawNetDeltaTotal,
      netDeltaOptions: formatSignedNative(rawNetDeltaOptions, row.currency, 4),
      netDeltaFutures: formatSignedNative(rawNetDeltaFutures, row.currency, 4),
      netDeltaTotal: formatSignedNative(rawNetDeltaTotal, row.currency, 4),
    };
  });
};

export const getPortfolioExposureRows = (accountData) => {
  if (hasStructuredPortfolioExposure(accountData)) {
    return (accountData?.portfolio_exposure?.rows || []).map((row) => {
      const wallet = buildStructuredExposureRow(row, 'wallet_exposure');
      const options = buildStructuredExposureRow(row, 'options_exposure');
      const futures = buildStructuredExposureRow(row, 'futures_exposure');
      const portfolio = buildStructuredExposureRow(row, 'net_portfolio_exposure');
      return {
        currency: row.currency,
        wallet: {
          nativeValue: row.wallet_exposure_native,
          nativeLabel: wallet.nativeLabel,
          exposurePct: row.wallet_exposure_pct,
          exposureLabel: formatUnsignedPercent(row.wallet_exposure_pct),
          bias: getRiskSummaryBiasMeta(row.wallet_exposure_pct),
        },
        options: {
          netDeltaValue: row.options_exposure_native,
          netDeltaLabel: options.nativeLabel,
          exposurePct: row.options_exposure_pct,
          exposureLabel: options.exposureLabel,
          bias: options.summaryBias,
        },
        futures: {
          netDeltaValue: row.futures_exposure_native,
          netDeltaLabel: futures.nativeLabel,
          exposurePct: row.futures_exposure_pct,
          exposureLabel: futures.exposureLabel,
          bias: futures.summaryBias,
        },
        portfolio: {
          nativeValue: row.net_portfolio_exposure_native,
          nativeLabel: portfolio.nativeLabel,
          exposurePct: row.net_portfolio_exposure_pct,
          exposureLabel: portfolio.exposureLabel,
          bias: portfolio.summaryBias,
          markerPosition: portfolio.markerPosition,
        },
      };
    }).filter((row) => Number(row.wallet.nativeValue || 0) !== 0 || Number(row.options.netDeltaValue || 0) !== 0 || Number(row.futures.netDeltaValue || 0) !== 0 || Number(row.portfolio.nativeValue || 0) !== 0);
  }

  const walletItems = getWalletExposureItems(accountData);
  const positionRows = getPositionRiskRows(accountData);
  const totalWalletUsd = walletItems.reduce((sum, item) => sum + Number(item.valueUsd || 0), 0);
  const walletByCurrency = new Map(walletItems.map((item) => [String(item.currency).toUpperCase(), item]));
  const positionByCurrency = new Map(positionRows.map((row) => [String(row.currency).toUpperCase(), row]));
  const currencies = getExposureDisplayOrder([
    ...walletItems.filter((item) => !item.isStable && Number(item.valueUsd || 0) > 0).map((item) => item.currency),
    ...positionRows.filter((row) => !isStableCurrency(row.currency)).map((row) => row.currency),
  ]);

  return currencies
    .map((currency) => {
      const wallet = walletByCurrency.get(currency) || null;
      const position = positionByCurrency.get(currency) || null;
      const walletNativeValue = wallet ? Number(wallet.directionalNativeValue || 0) : 0;
      const walletExposurePct = wallet ? Number(wallet.percent || 0) : 0;
      const positionNativeAvailable = position ? position.rawNetDelta !== null && position.rawNetDelta !== undefined : true;
      const positionNativeValue = positionNativeAvailable ? Number(position?.rawNetDelta || 0) : null;
      const portfolioNativeValue = positionNativeAvailable ? Number((walletNativeValue + Number(positionNativeValue || 0)).toFixed(8)) : null;
      const usdPrice = wallet?.usdPrice ?? position?.usdPrice ?? null;
      const canComputePortfolioPct = Boolean(
        totalWalletUsd > 0
        && usdPrice
        && portfolioNativeValue !== null
        && (!position || position.exposurePct !== null || Number(positionNativeValue || 0) === 0)
      );
      const portfolioExposurePct = canComputePortfolioPct
        ? Number(((portfolioNativeValue * Number(usdPrice)) / totalWalletUsd).toFixed(8))
        : null;

      return {
        currency,
        wallet: {
          nativeValue: walletNativeValue,
          nativeLabel: wallet ? formatSignedNative(walletNativeValue, currency, 4) : '—',
          exposurePct: wallet ? walletExposurePct : null,
          exposureLabel: wallet ? wallet.percentLabel : '—',
          bias: wallet ? wallet.bias : getRiskSummaryBiasMeta(0),
        },
        position: {
          netDeltaValue: positionNativeValue,
          netDeltaLabel: position?.netDelta ?? '—',
          exposurePct: position?.exposurePct ?? null,
          exposureLabel: position?.exposurePct === null || position?.exposurePct === undefined ? '—' : position.exposureLabel,
          bias: position ? getRiskSummaryBiasMeta(position.exposurePct) : getRiskSummaryBiasMeta(0),
        },
        portfolio: {
          nativeValue: portfolioNativeValue,
          nativeLabel: portfolioNativeValue === null ? '—' : formatSignedNative(portfolioNativeValue, currency, 4),
          exposurePct: portfolioExposurePct,
          exposureLabel: portfolioExposurePct === null ? '—' : formatSignedPercent(portfolioExposurePct),
          bias: getRiskSummaryBiasMeta(portfolioExposurePct),
          markerPosition: getBiasMarkerPosition(portfolioExposurePct),
        },
      };
    })
    .filter((row) => Number(row.wallet.nativeValue || 0) !== 0 || Number(row.position.netDeltaValue || 0) !== 0 || Number(row.portfolio.nativeValue || 0) !== 0);
};